import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { usePopup } from '@/context/PopupContext';
import { ScrollArea } from '@/components/ui/scroll-area';

const pricingPlans = [
  {
    name: 'Essentials',
    price: '$45',
    subtitle: '+ Federal Filing Fee',
    features: [
      'Case Review',
      'Case Filing',
      'Basic Search',
      'State E-Filing',
      'Trademark Secured'
    ],
    note: 'Govt. filing fee not included',
    recommended: false
  },
  {
    name: 'Enhanced',
    price: '$149',
    subtitle: '+ Federal Filing Fee',
    features: [
      'Case Review',
      'Case Filing',
      'Direct Hit Search',
      'Trademark Alert',
      'Trademark Secured',
      'High Satisfaction Rate',
      'Refusal Risk Meter'
    ],
    note: 'Govt. filing fee not included',
    recommended: false
  },
  {
    name: 'Deluxe',
    price: '$299',
    subtitle: '+ Federal Filing Fee',
    features: [
      'Case Review',
      'Case Filing',
      'Direct Hit Search',
      'Trademark Alert',
      'Trademark Secured',
      'High Satisfaction Rate',
      'Refusal Risk Meter',
      'Trademark Monitoring',
      'Complete Documentation',
      'Digital File',
      'Dedicated Case Manager'
    ],
    note: 'Govt. filing fee not included',
    recommended: true
  },
  {
    name: 'Platinum',
    price: '$349',
    subtitle: '+ Federal Filing Fee',
    features: [
      'Case Review',
      'Case Filing',
      'Direct Hit Search',
      'Trademark Alert',
      'Trademark Secured',
      'High Satisfaction Rate',
      'Refusal Risk Meter',
      'Trademark Monitoring',
      'Complete Documentation',
      'Digital File',
      'Dedicated Case Manager',
      'High Approval Rate',
      'Comprehensive Search Report',
      'High Priority Service',
      'Cease & Desist Letter'
    ],
    note: 'Govt. filing fee not included',
    recommended: false
  }
];

export const Pricing = () => {
  const { openPopup } = usePopup();

  return (
    <section className="py-20 md:py-32 bg-brand-navy-light" id="pricing">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Cost-Effective Trademark Solutions
          </h2>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Choose the package that best fits your needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingPlans.map((plan, idx) => (
            <Card
              key={idx}
              className={`relative flex flex-col h-full transition-all duration-300 hover:-translate-y-2 bg-brand-navy-card group ${
                plan.recommended 
                  ? 'border-2 border-blue-500 shadow-lg shadow-blue-500/20 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]' 
                  : 'border border-blue-900/30 hover:border-blue-500 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]'
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {plan.recommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white border-0">
                  POPULAR
                </Badge>
              )}
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-white">{plan.name}</CardTitle>
                <CardDescription>
                  <span className="text-3xl font-bold text-blue-400">{plan.price}</span>
                  <span className="text-sm text-gray-400 block">{plan.subtitle}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <ScrollArea className="h-64">
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
                <p className="text-xs text-gray-500 mt-4 italic">{plan.note}</p>
              </CardContent>
              <CardFooter>
                <Button
                  onClick={openPopup}
                  data-testid={`select-plan-${plan.name.toLowerCase()}-button`}
                  className={`w-full font-medium transition-transform hover:scale-105 ${
                    plan.recommended
                      ? 'bg-blue-500 hover:bg-blue-600 text-white'
                      : 'bg-brand-navy border border-blue-900/50 hover:bg-blue-500/10 text-blue-400'
                  }`}
                >
                  Get Started
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};